import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import i18n from 'i18next';

const ToastContext = createContext(null);

const SEVERITY = { success: 'success', error: 'error', warning: 'warning', info: 'info' };

function translateIfKey(msg) {
  if (typeof msg === 'string' && (msg.startsWith('errors.') || msg.startsWith('auth.') || msg.startsWith('common.'))) {
    return i18n.t(msg);
  }
  return msg;
}

export function ToastProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');

  const show = useCallback((msg, sev = 'info') => {
    setMessage(msg || '');
    setSeverity(sev);
    setOpen(true);
  }, []);

  const success = useCallback((msg) => show(msg, SEVERITY.success), [show]);
  const error = useCallback((msg) => show(msg, SEVERITY.error), [show]);
  const warning = useCallback((msg) => show(msg, SEVERITY.warning), [show]);
  const info = useCallback((msg) => show(msg, SEVERITY.info), [show]);

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const value = { show, success, error, warning, info };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleClose} severity={severity} variant="filled" sx={{ width: '100%' }}>
          {translateIfKey(message)}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export default ToastContext;
